import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import axios from 'axios';
import { CapacitorHttp } from '@capacitor/core';
import type { User, LoginRequest, LoginResponse, AuthState } from '../types';
import { isNativePlatform } from '../utils/platform';
import { setUserTimezone } from '../utils/timezone';
import { clearAuthenticatedSessionCache } from '../auth/sessionBoundary';

function getApiBaseUrl(): string {
  if (
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    window.location.hostname === 'localhost' &&
    !window.location.port
  ) {
    return import.meta.env.VITE_MOBILE_API_URL || 'https://neimus.ai/api/v1';
  }

  if (import.meta.env.VITE_API_URL) return import.meta.env.VITE_API_URL;

  return '/api/v1';
}

const API_BASE_URL = getApiBaseUrl();

async function nativePost<T>(url: string, data: unknown, headers?: Record<string, string>): Promise<{ data: T }> {
  if (isNativePlatform()) {
    const result = await CapacitorHttp.post({
      url,
      headers: { 'Content-Type': 'application/json', ...headers },
      data: data as Record<string, unknown>,
    });
    if (result.status >= 400) {
      const detail = result.data?.detail || result.data?.error || `Request failed (${result.status})`;
      const err = new Error(detail) as Error & { response: { data: unknown; status: number } };
      err.response = { data: result.data, status: result.status };
      throw err;
    }
    return { data: result.data as T };
  }
  return axios.post<T>(url, data, headers ? { headers } : undefined) as Promise<{ data: T }>;
}

async function nativeGet<T>(url: string, headers?: Record<string, string>): Promise<{ data: T }> {
  if (isNativePlatform()) {
    const result = await CapacitorHttp.get({
      url,
      headers: { ...headers },
    });
    if (result.status >= 400) {
      const detail = result.data?.detail || result.data?.error || `Request failed (${result.status})`;
      const err = new Error(detail) as Error & { response: { data: unknown; status: number } };
      err.response = { data: result.data, status: result.status };
      throw err;
    }
    return { data: result.data as T };
  }
  return axios.get<T>(url, headers ? { headers } : undefined) as Promise<{ data: T }>;
}

interface AuthActions {
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: User) => void;
  refreshUser: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  _hasHydrated: false,
};

// Audit Finding #H9: previously every auth field — including the
// access AND refresh tokens — was persisted to localStorage, where
// any successful XSS attack could exfiltrate or replay them
// indefinitely. We now store tokens in sessionStorage so they at
// least clear when the browser/tab closes, while keeping the (less
// sensitive) `user` profile in localStorage for refresh-without-login
// UX. Native (Capacitor) keeps its existing storage because the
// webview cannot share sessionStorage between app launches.
const TOKEN_STORAGE_KEY = 'neimus360-auth-tokens';
const PROFILE_STORAGE_KEY = 'neimus360-auth';

type TokenBundle = {
  accessToken: string | null;
  refreshToken: string | null;
};

function getTokenStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return isNativePlatform() ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function readPersistedTokens(): TokenBundle {
  const storage = getTokenStorage();
  if (!storage) return { accessToken: null, refreshToken: null };
  try {
    const raw = storage.getItem(TOKEN_STORAGE_KEY);
    if (!raw) return { accessToken: null, refreshToken: null };
    const parsed = JSON.parse(raw) as Partial<TokenBundle>;
    return {
      accessToken: parsed.accessToken ?? null,
      refreshToken: parsed.refreshToken ?? null,
    };
  } catch {
    return { accessToken: null, refreshToken: null };
  }
}

function writePersistedTokens(tokens: TokenBundle): void {
  const storage = getTokenStorage();
  if (!storage) return;
  try {
    if (!tokens.accessToken && !tokens.refreshToken) {
      storage.removeItem(TOKEN_STORAGE_KEY);
      return;
    }
    storage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
  } catch {
    // Storage may be unavailable in private mode; tokens just won't survive reload.
  }
}

function clearPersistedTokens(): void {
  const storage = getTokenStorage();
  if (!storage) return;
  try {
    storage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      login: async (credentials: LoginRequest) => {
        set({ isLoading: true });
        try {
          const response = await nativePost<LoginResponse>(`${API_BASE_URL}/auth/login`, credentials);
          const { access_token, refresh_token, user } = response.data;

          clearAuthenticatedSessionCache();
          set({
            user,
            accessToken: access_token,
            refreshToken: refresh_token,
            isAuthenticated: true,
            isLoading: false,
          });

          writePersistedTokens({
            accessToken: access_token,
            refreshToken: refresh_token,
          });

          // Profile snapshot in localStorage so a hard reload keeps the
          // UI populated until the next /auth/me roundtrip. NOTE: this
          // intentionally no longer contains tokens.
          try {
            localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify({
              state: { user, isAuthenticated: true },
              version: 0,
            }));
          } catch {
            // ignore — non-fatal
          }
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: () => {
        const token = get().accessToken;
        if (token) {
          nativePost(`${API_BASE_URL}/auth/logout`, {}, {
            Authorization: `Bearer ${token}`,
          }).catch(() => {});
        }

        try {
          localStorage.removeItem(PROFILE_STORAGE_KEY);
        } catch {
          // ignore
        }
        clearPersistedTokens();
        clearAuthenticatedSessionCache();
        set({ ...initialState, _hasHydrated: true });
      },

      setTokens: (accessToken: string, refreshToken: string) => {
        set({ accessToken, refreshToken });
        writePersistedTokens({ accessToken, refreshToken });
      },

      setUser: (user: User) => {
        clearAuthenticatedSessionCache();
        set({ user, isAuthenticated: true });
      },

      refreshUser: async () => {
        const token = get().accessToken;
        if (!token) {
          get().logout();
          throw new Error('No access token');
        }
        try {
          const response = await nativeGet<User>(`${API_BASE_URL}/auth/me`, {
            Authorization: `Bearer ${token}`,
          });
          set({ user: response.data, isAuthenticated: true });
          // Sync timezone
          if (response.data.timezone) {
            setUserTimezone(response.data.timezone);
          }
        } catch (error) {
          // Invalid/expired credentials end the session. Transient network or
          // server failures keep the tokens so invite hydration can be retried
          // without repeating account creation and 2FA setup.
          const responseStatus = (error as { response?: { status?: number } })
            .response?.status;
          if (responseStatus === 401 || responseStatus === 403) {
            get().logout();
          }
          throw error;
        }
      },

      checkAuth: async () => {
        const token = get().accessToken;
        if (!token) {
          return false;
        }

        try {
          await get().refreshUser();
          set({ isAuthenticated: true });
          return true;
        } catch {
          set({ isAuthenticated: false });
          return false;
        }
      },
    }),
    {
      name: PROFILE_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Audit Finding #H9: never persist tokens via this localStorage
      // snapshot. Tokens live in sessionStorage (web) / localStorage
      // (native) keyed under TOKEN_STORAGE_KEY and are rehydrated by
      // onRehydrateStorage below.
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const tokens = readPersistedTokens();
        state.accessToken = tokens.accessToken;
        state.refreshToken = tokens.refreshToken;
        if (!tokens.accessToken) {
          // No live tokens → ignore the (possibly stale) profile snapshot
          // so the user is prompted to log in again.
          state.user = null;
          state.isAuthenticated = false;
        }
      },
    }
  )
);

// ---------- Hydration tracking (safe pattern) ----------
// Zustand's onRehydrateStorage fires during create(), when the store variable
// isn't assigned yet. Instead, set _hasHydrated AFTER the store exists using
// the persist API's onFinishHydration, and check hasHydrated() for the
// initial value in case hydration already completed synchronously.
const _postHydrate = () => {
  useAuthStore.setState({ _hasHydrated: true });
};
// If hydration already finished (sync localStorage), set immediately.
if (useAuthStore.persist.hasHydrated()) {
  _postHydrate();
} else {
  useAuthStore.persist.onFinishHydration(_postHydrate);
}

// Selector hooks for common patterns
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useIsAdmin = () =>
  useAuthStore((state) =>
    // System admin (Tier 1)
    (state.user?.account_type === 'SYSTEM' && state.user?.is_admin) ||
    // Tenant admin (Tier 4)
    state.user?.is_tenant_admin ||
    state.user?.tenant_role === 'ADMIN' ||
    // Responder command admin (Tier 2)
    (state.user?.account_type === 'RESPONDER' && state.user?.responder_role === 'COMMAND_ADMIN')
  );

// Role-specific selectors
export const useIsSystemAdmin = () =>
  useAuthStore((state) => state.user?.account_type === 'SYSTEM' && state.user?.is_admin);

export const useIsResponder = () =>
  useAuthStore((state) => state.user?.account_type === 'RESPONDER');

export const useIsTenantAdmin = () =>
  useAuthStore((state) =>
    state.user?.is_tenant_admin || state.user?.tenant_role === 'ADMIN'
  );

export const useIsTenantUser = () =>
  useAuthStore((state) =>
    state.user?.account_type === 'TENANT' && !state.user?.is_tenant_admin
  );

// Get user tier (1-5)
export const useUserTier = () =>
  useAuthStore((state) => {
    const user = state.user;
    if (!user) return null;

    // Tier 1: Global Super Admin
    if (user.account_type === 'SYSTEM' && user.is_admin) return 1;

    // Tier 2: District/Agency Admin
    if (user.account_type === 'RESPONDER' && user.responder_role === 'COMMAND_ADMIN') return 2;

    // Tier 3: First Responder
    if (user.account_type === 'RESPONDER') return 3;

    // Tier 4: Tenant Admin
    if (user.is_tenant_admin || user.tenant_role === 'ADMIN') return 4;

    // Tier 5: Tenant User
    return 5;
  });
